import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Activity, RefreshCw, CheckCircle2, XCircle, HelpCircle, Play, StopCircle } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * CIHealthBadge — live green/red heartbeat for the pytest suite.
 *
 * Reads the last recorded pytest summary from `GET /api/admin/health/tests`.
 * The recorder is `backend/scripts/record_pytest_health.py` and should be
 * piped the output of any `pytest tests/` run.
 *
 * Also offers a one-click "Run Tests" button that hits
 * `POST /api/admin/health/tests/run` and opens a WebSocket to
 * `/api/admin/health/tests/stream` for live per-test progress. When any
 * test fails mid-run, the badge flips red immediately.
 *
 * Design intent: give the founder a single-glance signal on Command Centre
 * home so regressions don't hide until a specific flow breaks in prod.
 */

const ONE_MIN = 60 * 1000;
const REFRESH_MS = 60 * 1000; // auto-refresh once a minute

function timeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  if (diff < ONE_MIN) return 'just now';
  const mins = Math.floor(diff / ONE_MIN);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const CIHealthBadge = () => {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Live-run state
  const [running, setRunning] = useState(false);
  const [live, setLive] = useState(null); // {passed, failed, skipped, current_file, current_test}
  const [runError, setRunError] = useState(null);
  // When the founder clicks "Refresh from preview" on prod, we don't stream
  // anything — the mirror fetch completes in <1s and the badge just needs
  // a visual confirmation that the click did something. Cleared 4s later.
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const [latest, hist] = await Promise.all([
        apiClient.request('/api/admin/health/tests'),
        apiClient.request('/api/admin/health/tests/history?limit=10').catch(() => ({ runs: [] })),
      ]);
      setData(latest);
      setHistory(Array.isArray(hist?.runs) ? hist.runs : []);
    } catch (err) {
      setData({ status: 'error', message: err?.message || 'Failed to fetch' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const closeSocket = () => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
    }
  };

  const openStream = useCallback(() => {
    closeSocket();
    // Build ws:// or wss:// URL. `REACT_APP_BACKEND_URL` is https://…
    const wsBase = API_BASE.replace(/^http/, 'ws');
    // Backend accepts JWT via either Sec-WebSocket-Protocol OR ?token=…
    // Browsers can't set the Auth header on WS, so we pass by query string.
    const token = localStorage.getItem('token') || '';
    const url = `${wsBase}/api/admin/health/tests/stream?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'hello') {
          setRunning(true);
          setRunError(null);
          setLive({ passed: 0, failed: 0, skipped: 0 });
        } else if (msg.type === 'tick') {
          setLive({
            passed: msg.passed,
            failed: msg.failed,
            skipped: msg.skipped,
            current_file: msg.current_file,
            current_test: msg.current_test,
            last_status: msg.last_status,
          });
        } else if (msg.type === 'done') {
          setLive({
            passed: msg.passed,
            failed: msg.failed,
            skipped: msg.skipped,
            duration_s: msg.duration_s,
            done: true,
          });
          setRunning(false);
          // Reload authoritative data from the endpoint so `data.ran_at` refreshes.
          load();
          closeSocket();
        } else if (msg.type === 'error') {
          setRunError(msg.message || 'stream error');
          setRunning(false);
          closeSocket();
        }
      } catch {
        /* non-JSON frames are ignored */
      }
    };
    ws.onerror = () => {
      setRunError('WebSocket error');
    };
    ws.onclose = () => {
      // If it closes before "done", we simply stop showing running state.
      if (running) setRunning(false);
    };
  }, [load, running]);

  const startRun = useCallback(async () => {
    setRunError(null);
    try {
      const res = await apiClient.request('/api/admin/health/tests/run', { method: 'POST' });
      // Mirror-mode short-circuit (prod): backend returns
      // { status: "mirrored", source: "https://.../preview..." } instead
      // of starting a pytest process. Just reload the badge from the
      // freshly-persisted `test_health` row — no WebSocket needed.
      // If the mirror fetch itself failed the backend returns 200 with
      // { status: "mirror_failed", error } — surface it as a runError.
      if (res?.status === 'mirror_failed') {
        setRunError(res.error || 'Mirror fetch failed');
        return;
      }
      if (res?.status === 'mirrored') {
        setLastRefreshAt(new Date());
        load();
        return;
      }
      if (res?.run_id) {
        setRunning(true);
        setLive({ passed: 0, failed: 0, skipped: 0 });
        openStream();
      } else if (res?.detail) {
        setRunError(typeof res.detail === 'string' ? res.detail : (res.detail.message || 'Failed to start'));
      }
    } catch (err) {
      // 409 = already running → attach to the stream anyway.
      if (String(err?.message || '').includes('409')) {
        setRunning(true);
        openStream();
      } else {
        setRunError(err?.message || 'Failed to start');
      }
    }
  }, [openStream, load]);

  // On mount: load + check if a run is already in flight; if yes, attach.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      try {
        const rs = await apiClient.request('/api/admin/health/tests/run/status');
        if (!cancelled && rs?.active) {
          setRunning(true);
          openStream();
        }
      } catch { /* ignore */ }
    })();
    const int = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(int);
      closeSocket();
    };
    // Verified mount-only setup: initial data fetch + reconnect-to-active-run
    // check + interval polling. `load` and `openStream` are both wrapped in
    // useCallback and are structurally stable; listing them here would still
    // be safe but would re-fire the interval-clear-and-reset dance on every
    // parent re-render, which is wasteful given they're mount-scoped.
  }, []);

  // 4-second flash confirmation after a mirror-fetch completes. The
  // "Refresh from preview" button hits the backend, gets an instant reply
  // (mirror-fetch is <1s), and we need a brief visual cue that the click
  // did something before the badge itself re-renders with the fresh data.
  useEffect(() => {
    if (!lastRefreshAt) return undefined;
    const t = setTimeout(() => setLastRefreshAt(null), 4000);
    return () => clearTimeout(t);
  }, [lastRefreshAt]);

  if (loading) {
    return (
      <div
        data-testid="ci-health-badge-loading"
        className="bg-white p-3 rounded-lg flex items-center gap-3"
        style={{ border: '2px solid #0F2A55' }}
      >
        <Activity size={18} className="animate-pulse" style={{ color: '#44403A' }} />
        <span className="text-sm" style={{ color: '#44403A' }}>Loading test suite health…</span>
      </div>
    );
  }

  // If a live run is happening, its live state overrides the persisted status.
  const showLive = running || (live && live.done);
  const displayFailed = showLive ? (live?.failed ?? 0) : (data?.failed ?? 0);
  const displayPassed = showLive ? (live?.passed ?? 0) : (data?.passed ?? '—');
  const displaySkipped = showLive ? (live?.skipped ?? 0) : (data?.skipped ?? 0);
  const total = (typeof displayPassed === 'number' ? displayPassed : 0) + displayFailed + displaySkipped;

  // Stale-data guard: production's nightly worker is disabled, so its last
  // persisted run would otherwise stay red forever. Older than 48h → treat
  // as no-data (grey) unless a live run overrides.
  const STALE_MS = 48 * 3600 * 1000;
  const isStalePersisted = !showLive && data?.ran_at
    && (Date.now() - new Date(data.ran_at).getTime() > STALE_MS);
  const status = showLive
    ? (displayFailed > 0 ? 'red' : 'green')
    : (isStalePersisted ? 'no_data' : (data?.status || 'no_data'));
  const isGreen = status === 'green';
  const isRed = status === 'red';
  const isUnknown = !isGreen && !isRed;

  const accent = isGreen ? '#2EAA5C' : isRed ? '#DC2626' : '#44403A';
  const bg = isGreen ? '#F0FAF3' : isRed ? '#FEF2F2' : '#F8F8F8';
  const StatusIcon = isGreen ? CheckCircle2 : isRed ? XCircle : HelpCircle;

  // Dark navy border requested by founder — visually anchors the badge in
  // the whitespace under the Stripe card. Kept regardless of green/red state
  // so the widget always reads as a first-class Command Centre pillar.
  const NAVY = '#0F2A55';

  // Mirror-mode detection — when prod fetches preview's heartbeat, the
  // persisted row is tagged `source: "nightly-mirror"`. The button relabels
  // itself to "Refresh from preview" so the founder knows the click won't
  // spawn a 5-minute pytest run against the live DB.
  const isMirrorMode = data?.source === 'nightly-mirror';

  return (
    <div
      data-testid="ci-health-badge"
      className="p-3 rounded-lg flex items-center justify-between gap-3"
      style={{ backgroundColor: bg, border: `2px solid ${NAVY}` }}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          data-testid="ci-health-badge-status-icon"
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${accent}20` }}
        >
          <span className="relative flex items-center justify-center">
            {(isGreen || running) && (
              <span
                className="absolute inline-flex h-3 w-3 rounded-full opacity-75 animate-ping"
                style={{ backgroundColor: accent }}
              />
            )}
            <StatusIcon size={20} style={{ color: accent }} />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid="ci-health-badge-headline"
              className="text-sm font-bold"
              style={{ color: '#1A1209' }}
            >
              {running && !live?.done && `Running · ${displayPassed}/${total} so far`}
              {!running && isGreen && `All green · ${displayPassed}/${total} passing`}
              {!running && isRed && `${displayFailed} failing · ${displayPassed}/${total} passing`}
              {!running && isUnknown && (
                status === 'no_data'
                  ? 'No test run recorded yet'
                  : (data?.message || 'Status unavailable')
              )}
            </span>
            {history.length > 0 && (
              <span
                data-testid="ci-health-badge-sparkline"
                className="inline-flex items-center gap-0.5"
                title={`Last ${history.length} runs (oldest → newest)`}
              >
                {history.map((run, idx) => {
                  const dotColor = run.status === 'green' ? '#2EAA5C' : '#DC2626';
                  const label = `${run.status === 'green' ? 'pass' : 'fail'} · ${run.passed}/${(run.passed || 0) + (run.failed || 0)} · ${new Date(run.ran_at).toLocaleString()}`;
                  return (
                    <span
                      key={`${run.ran_at}-${idx}`}
                      data-testid={`ci-health-sparkline-dot-${idx}`}
                      title={label}
                      className="inline-block w-1.5 h-4 rounded-sm"
                      style={{ backgroundColor: dotColor }}
                    />
                  );
                })}
              </span>
            )}
            {displaySkipped > 0 && (
              <span
                data-testid="ci-health-badge-skipped"
                className="text-xs px-1.5 py-0.5 rounded"
                style={{ backgroundColor: '#F5F5F5', color: '#44403A' }}
              >
                {displaySkipped} skipped
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs mt-0.5 truncate" style={{ color: '#44403A' }}>
            {running && live?.current_file ? (
              <span data-testid="ci-health-badge-current" className="truncate">
                {live.current_file}
                {live.current_test ? ` · ${live.current_test}` : ''}
              </span>
            ) : (
              <>
                <span data-testid="ci-health-badge-timestamp">
                  {data?.ran_at ? `updated ${timeAgo(data.ran_at)}` : 'run the recorder to seed'}
                </span>
                {data?.duration_s != null && (
                  <>
                    <span>·</span>
                    <span data-testid="ci-health-badge-duration">
                      {data.duration_s < 60
                        ? `${data.duration_s.toFixed(1)}s`
                        : `${Math.floor(data.duration_s / 60)}m ${Math.round(data.duration_s % 60)}s`}
                    </span>
                  </>
                )}
              </>
            )}
            {runError && (
              <span data-testid="ci-health-badge-error" className="text-red-600">
                · {runError}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {!running ? (
          <button
            data-testid={isMirrorMode ? 'ci-health-badge-refresh-mirror' : 'ci-health-badge-run'}
            onClick={startRun}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors"
            style={{ backgroundColor: accent, color: 'white' }}
            title={
              isMirrorMode
                ? `Fetch the latest test status from ${data?.mirror_source_url || 'preview'} — production never runs pytest against the live DB`
                : 'Run pytest tests/ now and stream progress'
            }
          >
            {lastRefreshAt ? (
              <>
                <CheckCircle2 size={14} />
                Refreshed
              </>
            ) : isMirrorMode ? (
              <>
                <RefreshCw size={14} />
                Refresh from preview
              </>
            ) : (
              <>
                <Play size={14} />
                Run tests
              </>
            )}
          </button>
        ) : (
          <span
            data-testid="ci-health-badge-running-indicator"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold"
            style={{ backgroundColor: '#F5F5F5', color: '#44403A' }}
          >
            <StopCircle size={14} className="animate-pulse" />
            Streaming…
          </span>
        )}
        <button
          data-testid="ci-health-badge-refresh"
          onClick={load}
          disabled={refreshing}
          className="p-2 rounded hover:bg-white transition-colors disabled:opacity-50"
          title="Refresh test suite health"
        >
          <RefreshCw
            size={16}
            className={refreshing ? 'animate-spin' : ''}
            style={{ color: '#44403A' }}
          />
        </button>
      </div>
    </div>
  );
};

export default CIHealthBadge;

